import React, { Fragment, useState } from 'react';
import { ChevronRightIcon, FileTextIcon } from 'lucide-react';
import { StatementLoan } from '../../types/commission';
import { formatBps, formatUsd, sumCents } from '../../utils/format';
import { Badge } from '../sf/Badge';
import { StoredReference } from '../sf/StoredReference';
import { Button } from '../sf/Button';
import { NewBadge } from '../sf/NewBadge';
import { Card } from '../sf/Card';
import { Td, Th } from '../sf/DataTable';
import { Money } from '../sf/Money';
export function StatementLoansTable({
  loans
}: {
  loans: StatementLoan[];
}) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(['LN-2026-004744', 'LN-2026-004790']));
  const allExpanded = expanded.size === loans.length;
  const toggle = (loanNumber: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(loanNumber)) next.delete(loanNumber);else next.add(loanNumber);
    return next;
  });
  const totals = {
    amount: sumCents(loans.map(loan => loan.loanAmount)),
    adjusted: sumCents(loans.map(loan => loan.adjustedAmount)),
    pay: sumCents(loans.map(loan => loan.payTotal)),
    adjustedComp: sumCents(loans.map(loan => loan.adjustedTotal))
  };
  return <Card title={`Loans funded (${loans.length})`} icon={FileTextIcon} iconBg="#3BA755" meta="Expand a loan to see the commission lines behind it" bodyClassName="" actions={<Button size="sm" onClick={() => setExpanded(allExpanded ? new Set() : new Set(loans.map(loan => loan.loanNumber)))} data-id="element-967" data-mp="2f9wb36t3s1wg">
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </Button>} data-id="element-966" data-mp="26d58scnyjqtf">
      <div className="overflow-x-auto" data-id="element-968" data-mp="2u4j2alxuhsfz">
        <table className="w-full border-collapse" data-id="element-969" data-mp="2u3hr8hgtx5y9">
          <thead data-id="element-970" data-mp="urorid2npmhu">
            <tr data-id="element-971" data-mp="us85xjt3lsr4">
              <Th className="w-10" data-id="element-972" data-mp="usqcb99h5ncr">
                <span className="sr-only" data-id="element-973" data-mp="utrqhmlsepyp">Expand</span>
              </Th>
              <Th data-id="element-974" data-mp="zrkv4b7fzbwv">Loan #</Th>
              <Th data-id="element-975" data-mp="zsvzac4suloi">Borrower</Th>
              <Th data-id="element-976" data-mp="rpu3061emdnh">Funded</Th>
              <Th align="right" data-id="element-977" data-mp="rr55m2j45fd0">Loan Amount</Th>
              <Th align="right" data-id="element-978" data-mp="rrwwj01rmowy">Adjusted Amount</Th>
              <Th align="right" data-id="element-979" data-mp="msam0ydbnmru">BPS</Th>
              <Th align="right" data-id="element-980" data-mp="mtdbd1uzxh5n">
                Adjusted
                <span className="block text-[11px] font-normal text-sf-weak" data-id="element-981" data-mp="11p1cuj8xjq5u">adj. loan × BPS · pays</span>
              </Th>
              <Th align="right" className="font-normal" data-id="element-982" data-mp="wnvi6fvplsto">
                Stored (unadjusted)
                <span className="block text-[11px] font-normal text-sf-weak" data-id="element-983" data-mp="1an4yrgntuoy6">reference</span>
              </Th>
            </tr>
          </thead>
          <tbody data-id="element-984" data-mp="1kehpu61sxb35">
            {loans.map(loan => {
            const isOpen = expanded.has(loan.loanNumber);
            const rowId = `lines-${loan.loanNumber}`;
            return <Fragment key={loan.loanNumber} data-id="element-985" data-mp="1tf63v0r73bg1">
                  <tr className={isOpen ? 'bg-[#FAFAF9]' : ''} data-id="element-986" data-mp="1tfz0va0cqmkz">
                    <Td className="pr-0" data-id="element-987" data-mp="1penav3dkfzc9">
                      <button type="button" aria-expanded={isOpen} aria-controls={rowId} aria-label={`${isOpen ? 'Collapse' : 'Expand'} lines for ${loan.loanNumber}`} onClick={() => toggle(loan.loanNumber)} className="flex h-6 w-6 items-center justify-center rounded text-sf-weak transition-colors duration-150 ease-out hover:bg-sf-bg hover:text-sf-text" data-id="element-988" data-mp="1pfoxm53y2a9t">
                        <ChevronRightIcon className={`h-4 w-4 transition-transform duration-150 ease-out ${isOpen ? 'rotate-90' : ''}`} data-id="element-989" data-mp="1u2d8c5hk5f4m" />
                      </button>
                    </Td>
                    <Td className="whitespace-nowrap font-semibold text-sf-blue" data-id="element-990" data-mp="1g95iai94krp1">{loan.loanNumber}</Td>
                    <Td data-id="element-991" data-mp="1ka7lfa65xnwa">
                      <span className="flex flex-wrap items-center gap-1.5" data-id="element-992" data-mp="16c97g5vr7thb">
                        {loan.borrower}
                        {loan.negativeComp && <Badge tone="warning" data-id="element-993" data-mp="1bbcefvvao9rx">Negative comp</Badge>}
                        {loan.split && <>
                            <Badge tone="info" data-id="element-994" data-mp="29noqnyxldy0t">Split {loan.split.pct}%</Badge>
                            <NewBadge label="NEW" data-id="element-995" data-mp="29psy2qup9f5u" />
                          </>}
                      </span>
                    </Td>
                    <Td className="whitespace-nowrap" data-id="element-996" data-mp="1zwu2gp0scocq">{loan.fundedDate}</Td>
                    <Td align="right" data-id="element-997" data-mp="1uzboulbtpkye">{formatUsd(loan.loanAmount)}</Td>
                    <Td align="right" className={loan.negativeComp ? 'font-semibold text-sf-amber-text' : ''} data-id="element-998" data-mp="1uwfs30bpp2bj">
                      {formatUsd(loan.adjustedAmount)}
                    </Td>
                    <Td align="right" data-id="element-999" data-mp="1m086jjkhd9ky">
                      {loan.baseBps} <span className="text-sf-weak" data-id="element-1000" data-mp="1h02lwncmoncd">+ {loan.bonusBps}</span>
                    </Td>
                    <Td align="right" className="font-semibold" data-id="element-1001" data-mp="3gb3q1gx7e92i">
                      <Money value={loan.adjustedTotal} data-id="element-1002" data-mp="3bdbmfpn74yih" />
                    </Td>
                    <Td align="right" data-id="element-1003" data-mp="3bb7l9fgomdlw">
                      <StoredReference value={loan.payTotal} compact data-id="element-1004" data-mp="3ixcretpauuxt" />
                    </Td>
                  </tr>
                  {isOpen && <tr id={rowId} data-id="element-1005" data-mp="3ntl0pqgtftx2">
                      <td colSpan={9} className="border-b border-sf-border bg-[#FAFAF9] pb-3 pl-12 pr-3" data-id="element-1006" data-mp="3sluph3672nxr">
                        {(loan.split || loan.negativeComp) && <div className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-sf-weak" data-id="element-1007" data-mp="1hwbtzppopqv">
                            {loan.split && `Split ${loan.split.pct} / ${loan.split.partnerPct} with ${loan.split.partner} (splits are NEW — not in main today). Alex’s ${loan.split.pct}% is paid at his own rates; unit credit stays with Alex.`}
                            {loan.negativeComp && <>
                                <span data-id="element-1008" data-mp="p54gqgj89wsd">
                                  Total Comp Corr 0.60% ($1,192.80) − $1,395.00 halved fee short = −$202.20. Main uses ABS(): $202.20 ÷ 3.25% = Adjusted Loan Amount{' '}
                                  {formatUsd(loan.adjustedAmount)}. No floor.
                                </span>
                                <NewBadge label="NEW / requested: $0.00, flat $500 / $250 — not applied" data-id="element-1009" data-mp="2lb4zh1nok3hv" />
                              </>}
                          </div>}
                        <table className="w-full border-collapse overflow-hidden rounded border border-sf-border bg-white" data-id="element-1010" data-mp="26jebnqdk1tu6">
                          <thead data-id="element-1011" data-mp="22jc81bo9eqc9">
                            <tr data-id="element-1012" data-mp="22g7xlwk7ayd5">
                              <Th data-id="element-1013" data-mp="2g9frjuvr85y0">Component</Th>
                              <Th data-id="element-1014" data-mp="2gba9r1fn5jye">Rule Key</Th>
                              <Th align="right" data-id="element-1015" data-mp="2bj90zphombd4">BPS</Th>
                              <Th align="right" data-id="element-1016" data-mp="2bho78236ywgb">Adjusted (pays)</Th>
                              <Th align="right" className="font-normal" data-id="element-1017" data-mp="1nsd2k9iq0hww">
                                Stored – reference
                              </Th>
                            </tr>
                          </thead>
                          <tbody data-id="element-1018" data-mp="3ug429bnfmpre">
                            {loan.lines.map(line => <tr key={line.component} data-id="element-1019" data-mp="34kfgxe4xvqn">
                                <Td className="py-1.5" data-id="element-1020" data-mp="3lh8qlk1mcngp">
                                  {line.component}
                                  {loan.split && <span className="text-sf-weak" data-id="element-1021" data-mp="3qh4z9wl9h32l"> · {loan.split.pct}%</span>}
                                </Td>
                                <Td className="py-1.5 font-mono text-xs" data-id="element-1022" data-mp="i11p9k6of2b3">{line.ruleKey}</Td>
                                <Td align="right" className="py-1.5" data-id="element-1023" data-mp="m1bw0z2alwr0">
                                  {formatBps(line.bps, line.component === 'Monthly Bonus')}
                                </Td>
                                <Td align="right" className="py-1.5 font-semibold" data-id="element-1024" data-mp="czm5dvg8j1ua">
                                  <Money value={line.adjustedAmount} data-id="element-1025" data-mp="2srjqra0natgo" />
                                </Td>
                                <Td align="right" className="py-1.5" data-id="element-1026" data-mp="2xs7jq18fpkdj">
                                  <StoredReference value={line.payAmount} compact data-id="element-1027" data-mp="3pf968isc4x1p" />
                                </Td>
                              </tr>)}
                          </tbody>
                        </table>
                      </td>
                    </tr>}
                </Fragment>;
          })}
            <tr className="bg-[#FAFAF9] font-semibold" data-id="element-1028" data-mp="c031pfvec8gh">
              <Td data-id="element-1029" data-mp="bzjky2hhfq41" />
              <Td colSpan={3} data-id="element-1030" data-mp="c25prucznrik">
                Loan subtotal
              </Td>
              <Td align="right" data-id="element-1031" data-mp="71i0bd4eriqg">{formatUsd(totals.amount)}</Td>
              <Td align="right" data-id="element-1032" data-mp="29rpz75h0uz1">{formatUsd(totals.adjusted)}</Td>
              <Td data-id="element-1033" data-mp="27x1xjywkmqq" />
              <Td align="right" data-id="element-1034" data-mp="3tguc3k6gcb2e">
                <Money value={totals.adjustedComp} data-id="element-1035" data-mp="3tft27j169um9" />
              </Td>
              <Td align="right" data-id="element-1036" data-mp="ur40o3wio401">
                <StoredReference value={totals.pay} compact data-id="element-1037" data-mp="urcd5uoq97ap" />
              </Td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>;
}